import React, {
  useRef,
  useLayoutEffect,
  useState,
  useCallback,
  useMemo,
} from "react";
import useIsMobile from "../../hooks/useIsMobile";
import styles from "./ScaledHtmlPreview.module.css";
import { sanitizeDocumentHtml } from "../../helpers/sanitizeHtml";

const DEFAULT_DESIGN_WIDTH = 800;

const ScaledHtmlPreview = ({
  html,
  className = "",
  reflowOnMobile = false,
  designWidth = DEFAULT_DESIGN_WIDTH,
}) => {
  const isMobile = useIsMobile();
  const safeHtml = useMemo(() => sanitizeDocumentHtml(html), [html]);
  const outerRef = useRef(null);
  const innerRef = useRef(null);
  const [layout, setLayout] = useState({
    scale: 1,
    contentWidth: designWidth,
    contentHeight: 0,
    useFit: false,
  });

  const measure = useCallback(() => {
    if (reflowOnMobile && isMobile) {
      setLayout({
        scale: 1,
        contentWidth: designWidth,
        contentHeight: 0,
        useFit: false,
      });
      return;
    }

    const outer = outerRef.current;
    if (!outer || !html) return;

    const containerWidth = outer.clientWidth;
    if (!containerWidth) return;

    const shouldFit = containerWidth < designWidth - 8;
    if (!shouldFit) {
      setLayout({
        scale: 1,
        contentWidth: designWidth,
        contentHeight: 0,
        useFit: false,
      });
      return;
    }

    const probe = document.createElement("div");
    probe.className = styles.measureProbe;
    probe.style.width = `${designWidth}px`;
    probe.style.minWidth = `${designWidth}px`;
    probe.style.boxSizing = "border-box";
    probe.style.minHeight = "0";
    probe.style.height = "auto";
    probe.innerHTML = sanitizeDocumentHtml(html);

    probe.querySelectorAll("*").forEach((el) => {
      el.style.setProperty("min-height", "0", "important");
      el.style.setProperty("height", "auto", "important");
    });

    outer.appendChild(probe);

    const contentWidth = designWidth;
    // Use the probe's own measured document height to avoid oversized blanks.
    const contentHeight = Math.ceil(probe.scrollHeight);

    outer.removeChild(probe);

    if (!contentHeight) return;

    const scale = containerWidth / contentWidth;
    setLayout({ scale, contentWidth, contentHeight, useFit: true });
  }, [html, isMobile, reflowOnMobile, designWidth]);

  useLayoutEffect(() => {
    let frame = requestAnimationFrame(() => {
      frame = requestAnimationFrame(() => measure());
    });
    return () => cancelAnimationFrame(frame);
  }, [html, measure]);

  useLayoutEffect(() => {
    if (reflowOnMobile && isMobile) return undefined;

    const outer = outerRef.current;
    const inner = innerRef.current;
    if (!outer) return undefined;

    const ro = new ResizeObserver(() => measure());
    ro.observe(outer);

    const onImgLoad = () => measure();
    const imgs = inner?.querySelectorAll("img") ?? [];
    imgs.forEach((img) => {
      if (!img.complete) img.addEventListener("load", onImgLoad);
    });

    window.addEventListener("resize", measure);

    return () => {
      ro.disconnect();
      imgs.forEach((img) => img.removeEventListener("load", onImgLoad));
      window.removeEventListener("resize", measure);
    };
  }, [html, isMobile, measure, reflowOnMobile]);

  if (!html) return null;

  if (reflowOnMobile && isMobile) {
    return (
      <div
        ref={outerRef}
        className={`${styles.reflowOuter} scaled-html-reflow-outer ${className}`.trim()}
      >
        <div
          ref={innerRef}
          className={`${styles.reflowInner} scaled-html-reflow-inner`}
          dangerouslySetInnerHTML={{ __html: safeHtml }}
        />
      </div>
    );
  }

  const { scale, contentWidth, contentHeight, useFit } = layout;
  const ready = useFit && contentWidth > 0 && contentHeight > 0;

  if (!useFit && !isMobile) {
    return (
      <div
        ref={outerRef}
        className={className}
        dangerouslySetInnerHTML={{ __html: safeHtml }}
      />
    );
  }

  if (!useFit && isMobile) {
    return (
      <div
        ref={outerRef}
        className={`${styles.fitOuter} scaled-html-fit-outer ${className}`.trim()}
      >
        <div
          ref={innerRef}
          className={`${styles.fitInner} scaled-html-fit-inner`}
          style={{ width: "100%" }}
          dangerouslySetInnerHTML={{ __html: safeHtml }}
        />
      </div>
    );
  }

  return (
    <div
      ref={outerRef}
      className={`${styles.fitOuter} scaled-html-fit-outer ${className}`.trim()}
      style={
        ready
          ? {
              width: "100%",
              height: contentHeight * scale,
              overflow: "hidden",
            }
          : { width: "100%" }
      }
    >
      <div
        ref={innerRef}
        className={`${styles.fitInner} scaled-html-fit-inner`}
        style={
          ready
            ? {
                width: contentWidth,
                transform: `scale(${scale})`,
                transformOrigin: "top left",
              }
            : {
                width: designWidth,
                visibility: "hidden",
              }
        }
        dangerouslySetInnerHTML={{ __html: safeHtml }}
      />
    </div>
  );
};

export default ScaledHtmlPreview;
